// @flow

import _ from 'lodash';
import { createSelector } from 'reselect';

import getFlowWithSteps from 'app/beta-editor/utils/getFlowWithSteps';
import { ZAP_META_ID } from 'app/beta-editor/utils/MultiSampleUtils';
import { ensureArray } from 'app/common/CommonUtils';
import { getRootNode } from 'app/beta-editor/utils/NodeTrees';
import { legacyAccountAndProfileCombo } from 'app/account/Selectors';
import { selectSharedZapById } from 'app/shared-zap/Selectors';
import {
  ZAPIER_META_KEY_PREFIX,
  ZT_API_NAMESPACE,
} from 'app/beta-editor/constants';
import { getIntegerParam, getParam } from 'app/common/utils/RouterSelectors';
import {
  getPathColor,
  getPathEmoji,
  getPathName,
  getRootNodeOfPath,
  isPathNode,
} from 'app/beta-editor/utils/PathUtils';
import {
  getSharedZapId,
  isRemoving,
  mapNodeIdsToSharedZapStepIndexes,
} from 'app/beta-editor/utils/FlowUtils';

import type { AuthMap } from 'app/auth/types';
import type {
  FieldsPageContainerProps,
  Flow,
  FlowNode,
  FlowProps,
  FlowState,
  FlowTaskState,
  NodeMap,
} from 'app/beta-editor/flowState';
import type { ID, State } from 'app/common/types';
import type { PublicZap } from 'app/beta-editor/types';
import type {
  Sample,
  TriggerSamples,
} from 'app/beta-editor/components/SamplePage';

export { getErrors as selectErrors } from 'app/error/Selectors';

type IdProps = {| flowId?: ID, id: ID |};

type PropsWithCurrentFlowId = FlowProps | FieldsPageContainerProps | IdProps;

const selectCurrentFlowId = (state, props: PropsWithCurrentFlowId) => {
  if (props.flow) {
    return props.flow.id;
  }
  return props.flowId || props.id;
};

const selectFlows = state => _.get(state, ['flow', 'flows']);

export const selectIsRemoving = createSelector(
  selectFlows,
  flows => _.some(flows, isRemoving)
);

export const selectFlow = (id: ID | number, state: State): Flow => {
  return (selectFlows(state) || {})[id];
};

export const selectFlowTitle = (flow?: Flow) => _.get(flow, 'title', '');

export const selectNode = (
  state: State,
  props: { flowId: ID, nodeId: ID }
): FlowNode => {
  return _.get(selectFlow(props.flowId, state), ['nodes', props.nodeId]);
};

export const selectNodeWithPlanLimits = (state: State, flowId: ID) => {
  const flow = selectFlow(flowId, state);
  return _.values(flow.nodes).find(node => Boolean(node.plan_limits));
};

export const selectNodeByNumber = (
  state: State,
  props: { flowId: ID, stepNum: number }
): FlowNode => {
  return selectNode(state, {
    flowId: props.flowId,
    nodeId: _.get(selectFlow(props.flowId, state), ['nodeIds'], [])[
      props.stepNum - 1
    ],
  });
};

export const selectNodeAuthOwnerId = (
  state: State,
  props: { flowId: ID, nodeId: ID }
) => {
  return _.get(selectNode(state, props), 'authentication_owner_id');
};

export const selectNodePlanValidation = (
  state: State,
  props: { flowId: ID, nodeId: ID }
) => {
  return _.get(selectNode(state, props), 'plan_limits');
};

export const selectIsFlowTemplate = (
  state: State,
  props: { flowId: ID, nodeId: ID }
): boolean => {
  const _props = { flowId: props.flowId, nodeId: props.nodeId || props.flowId };
  return _.get(selectNode(state, _props), [ZT_API_NAMESPACE], false);
};

const selectSharedZaps = state => state.sharedZap.sharedZaps;

const selectCurrentSharedZap = createSelector(
  selectCurrentFlowId,
  selectFlows,
  selectSharedZaps,
  (flowId, flows, sharedZaps) => {
    const flow = flows[flowId];
    if (!flow) {
      return null;
    }
    const sharedZapId = getSharedZapId(flow);
    if (!sharedZapId) {
      return null;
    }
    return sharedZaps[sharedZapId];
  }
);

export const selectCurrentPublishForm = createSelector(
  selectCurrentFlowId,
  selectFlows,
  (flowId, flows) => _.get(flows, [flowId, 'publish'])
);

export const selectCurrentPublishStatus = createSelector(
  selectCurrentPublishForm,
  publish => _.get(publish, ['value', 'status'])
);

export const selectPublishMessages = createSelector(
  selectCurrentPublishForm,
  publish => _.get(publish, ['messages'])
);

export const selectPausedByPlanZaps = createSelector(
  selectFlows,
  flows => _.filter(flows, zap => zap.paused && zap.paused_by_plan)
);

export const selectPausedByPlanZapIds = createSelector(
  selectFlows,
  flows =>
    _.reduce(
      flows,
      (ids, zap) => {
        return zap.paused && zap.paused_by_plan ? ids.concat(zap.id) : ids;
      },
      []
    )
);

export const flowIsLoaded = (state: State) =>
  _.get(state, ['flow', 'isLoaded']);

export const flowIsLoading = (state: State) =>
  _.get(state, ['flow', 'isLoading']);

export const getNodeAppSampleData = (flow: Flow, nodeId: ID) => {
  const samples = _.castArray(_.get(flow, ['appSample', nodeId], []));
  return samples.map(sample =>
    _.omitBy(sample, (val, key: string) =>
      key.startsWith(ZAPIER_META_KEY_PREFIX)
    )
  );
};

export const compactNodeAppSampleData = (samples: Array<*> = []) =>
  samples.reduce((prev, curr) => ({ ...prev, ...curr }), {});

export const getCompactAppSampleDataForNode = (flow: Flow, nodeId: ID) => {
  const sampleData = getNodeAppSampleData(flow, nodeId);
  const compacted = compactNodeAppSampleData(sampleData);
  return compacted;
};

export const isAppSampleDataLoadingForNode = (
  flow: Flow,
  nodeId: ID,
  isZapTemplate: boolean
) => {
  const sampleData = getCompactAppSampleDataForNode(flow, nodeId);
  return isZapTemplate && Boolean(sampleData) && Boolean(sampleData.isLoading);
};

export const selectNodeAppSampleData = createSelector(
  (state, props) => {
    const flowId = props.flow ? props.flow.id : props.flowId;
    return selectFlow(flowId, state);
  },
  (state, { nodeId }) => nodeId,
  getNodeAppSampleData
);

export const selectCompactNodeAppSampleData = createSelector(
  selectNodeAppSampleData,
  nodeSampleData => compactNodeAppSampleData(nodeSampleData)
);

export const isNodeAppSampleDataLoading = createSelector(
  (state, { isZapTemplate }) => isZapTemplate,
  selectCompactNodeAppSampleData,
  (isZapTemplate, sampleData) =>
    isZapTemplate && Boolean(sampleData) && Boolean(sampleData.isLoading)
);

export const selectNodeHasAppSampleData = createSelector(
  selectCompactNodeAppSampleData,
  isNodeAppSampleDataLoading,
  (sampleData, isLoading) => !isLoading && !_.isEmpty(sampleData)
);

export const selectHasBeenLive = createSelector(
  selectFlow,
  flow => !!flow.last_live_at
);

export const selectIsToggling = createSelector(
  selectFlows,
  (state, props) => props,
  (flows, { flowId }) => (flows[flowId] ? flows[flowId].isToggling : false)
);

export const isOnAuthPage = (state: State) => {
  return getParam('pageKey', state) === 'auth';
};

export const isOnAuthPageForNodeId = (state: State, nodeId: ID) => {
  const isOnPageForNode =
    getParam('nodeId', state) === nodeId ||
    getIntegerParam('nodeId', state) === nodeId;
  return isOnAuthPage(state) && isOnPageForNode;
};

export const hasAuthenticationInNode = (
  state: State,
  props: { nodeId: ID, flowId: ID }
) => {
  const node = selectNode(state, props);
  return !!_.get(node, ['authentication_id']);
};

export const hasRestrictedAuthInNode = ({
  auths,
  currentUserId,
  flow,
  nodeId,
}: {
  auths: AuthMap,
  currentUserId: ID,
  flow: Flow,
  nodeId: ID,
}): boolean => {
  const authId = _.get(flow, ['nodes', nodeId, 'authentication_id']);
  const authOwnerId = _.get(flow, ['nodes', nodeId, 'authentication_owner_id']);
  const auth = _.get(auths, authId);
  return (
    !!authId && // `authId` is actually set
    authOwnerId !== currentUserId && // and it belongs to another user
    !auth // and `auth` doesn't exist, meaning it's not shared
  );
};

const selectNodeId = (state, props) => props.nodeId;
const selectFlowId = (state, props) => props.flow.id;
const selectPropsFlow = props => props.flow;

export const getSelectedApis = createSelector(
  selectPropsFlow,
  flow => {
    const selectedApis = flow.nodeIds
      .map(nodeId => flow.nodes[nodeId].selected_api)
      .filter(Boolean)
      .join(',');
    return selectedApis;
  }
);

export const shouldShowPreviewModal = createSelector(
  selectFlows,
  selectFlowId,
  selectNodeId,
  (flows, flowId, nodeId) => {
    return _.get(
      flows,
      [flowId, 'fieldPageState', nodeId, 'shouldShowPreviewModal'],
      null
    );
  }
);

// bool box selectors -- will need to refactor these a bit

export const isTimeoutOnSample = createSelector(
  selectFlows,
  selectCurrentFlowId,
  (flows, flowId) =>
    _.get(flows, [flowId, 'sampleStates', 'isTimeoutOnSample'], false)
);

export const isWaitingForSample = createSelector(
  selectFlows,
  selectCurrentFlowId,
  (flows, flowId) =>
    _.get(flows, [flowId, 'sampleStates', 'isWaitingForSample'], false)
);

export const isPollingForSample = createSelector(
  selectFlows,
  selectCurrentFlowId,
  (flows, flowId) =>
    _.get(flows, [flowId, 'sampleStates', 'isPollingForSample'], false)
);

export const isSubscribing = createSelector(
  selectFlows,
  selectCurrentFlowId,
  selectNodeId,
  (flows, flowId, nodeId) =>
    _.get(flows, [flowId, 'sampleStates', 'isSubscribing', nodeId])
);

export const isRemovingSubscription = createSelector(
  selectFlows,
  selectCurrentFlowId,
  selectNodeId,
  (flows, flowId, nodeId) =>
    _.get(flows, [flowId, 'sampleStates', 'isRemovingSubscription', nodeId])
);

export const hasWaitedForSample = createSelector(
  selectFlows,
  selectCurrentFlowId,
  (flows, flowId) =>
    _.get(flows, [flowId, 'sampleStates', 'hasWaitedForSample'], false)
);

export const isRetryingSample = createSelector(
  selectFlows,
  selectCurrentFlowId,
  (flows, flowId) =>
    _.get(flows, [flowId, 'sampleStates', 'isRetryingSample'], false)
);

export const selectCurrentFlow = createSelector(
  selectFlows,
  selectCurrentFlowId,
  (flows, flowId) => _.get(flows, flowId)
);

// The value of nodeLoading[nodeId] is incremented anytime we're waiting
// for the node to be run/refresh/update and decrements when we're finished,
// so it's truthy if it's greater than zero.
export const isOneNodeLoading = (flow: Flow, nodeId: ID) =>
  !!_.get(flow, ['nodeLoading', nodeId]);

export const isNodeLoading = createSelector(
  selectCurrentFlow,
  selectNodeId,
  isOneNodeLoading
);

export const isAnyNodeLoading = createSelector(
  selectCurrentFlow,
  flow => {
    const nodeIds = _.keys(_.get(flow, 'nodes'));

    if (nodeIds.length === 0) {
      return true;
    }

    return _.some(nodeIds, nodeId => isOneNodeLoading(flow, nodeId));
  }
);

export const selectPathInfo = createSelector(
  selectFlows,
  selectCurrentFlowId,
  selectNodeId,
  (flows, flowId, nodeId) => {
    const flow = _.get(flows, flowId, {});
    if (_.isEmpty(flow) || _.isEmpty(flow.nodes[nodeId])) {
      // still loading
      return null;
    }

    const maybePathNode = getRootNodeOfPath(flowId, flow.nodes, nodeId);
    if (isPathNode(maybePathNode)) {
      return {
        name: getPathName(maybePathNode),
        color: getPathColor(maybePathNode),
        emoji: getPathEmoji(maybePathNode),
      };
    }
    return null;
  }
);

export const selectCopiedZaps = (state: State) => state.flow.copiedZaps;

export const selectLastCopiedZapId = (state: State) =>
  state.flow.lastCopiedZapId;

export const getTriggerSamples = (taskState: FlowTaskState, flowId: ID) => {
  const nodeOutput = _.compact(
    ensureArray(_.get(taskState, ['output', flowId]))
  );
  // we need to make sure that the samples are not empty objects,
  // and that they are not objects with only metadata in them,
  // which is what the backend will produce if someone
  // goes past their trigger/sample step without testing it
  // and then returns to the trigger/sample step.
  return nodeOutput.reduce((acc, smpl) => {
    return _.isObject(smpl) && !_.isEmpty(smpl) ? acc.concat(smpl) : acc;
  }, []);
};

export const getFirstTriggerSampleId = (triggerSamples: TriggerSamples) =>
  _.get(triggerSamples, ['0', ZAP_META_ID]);

export const getSelectedTriggerSampleId = (
  taskState: FlowTaskState,
  flowId: ID
) =>
  _.get(taskState, ['selected_sample']) ||
  getFirstTriggerSampleId(getTriggerSamples(taskState, flowId));

const hasMetaDataId = (sample: Sample) => {
  // if a sample had a zap meta id, that means it's been
  // through our system, so it's valid. If it doesn't
  // have a valid id, it could possibly be a fake sample,
  // which the server creates if you don't test a step
  // and then navigate away from it. This prevents nonexistant
  // samples from showing up in the editor after you navigate
  // away from a step and then come back to it.
  return _.get(sample, [ZAP_META_ID], false);
};

export const getValidTriggerSamples = (
  taskState: FlowTaskState,
  flowId: ID
) => {
  const samples = getTriggerSamples(taskState, flowId);
  return _.filter(samples, hasMetaDataId);
};

export const hasValidTriggerSamples = (
  taskState: FlowTaskState,
  flowId: ID
) => {
  const triggerSamplesWithValidIds = getValidTriggerSamples(taskState, flowId);
  return !_.isEmpty(triggerSamplesWithValidIds);
};

export const getCopiedZap = ({
  state,
  zap,
  result,
}: {
  state: FlowState,
  zap: { id: string },
  result: { id: number },
}) => {
  const prevCopiedZaps = _.get(state, ['copiedZaps'], {});
  const prevClonedZapIds = _.get(prevCopiedZaps, [zap.id], []);

  const key = Object.keys(prevCopiedZaps).filter(zapId =>
    prevCopiedZaps[zapId].includes(zap.id)
  )[0];

  if (!key) {
    return {
      [zap.id]: [result.id, ...prevClonedZapIds],
    };
  } else {
    const values = prevCopiedZaps[key];
    const index = _.findIndex(values, zapId => zap.id === zapId);
    const firstPart = values.slice(0, index + 1);
    const secondPart = values.slice(index + 1);
    return {
      [key]: [...firstPart, result.id, ...secondPart],
    };
  }
};

const selectCurrentFlowWithEmptyDefault = createSelector(
  selectFlows,
  selectCurrentFlowId,
  (flows, flowId) => _.get(flows, flowId, {})
);

export const selectFlowWithSteps = createSelector(
  selectCurrentFlowWithEmptyDefault,
  selectNodeId,
  (flow, nodeId) => getFlowWithSteps(flow, flow.id, flow.nodes, nodeId)
);

export const selectShouldUseSimpleZapSetup = createSelector(
  state =>
    _.get(state, ['router', 'current', 'query', 'simple'], false) === 'true',
  hasSimpleQueryParams => hasSimpleQueryParams
);

// Test Simple Zaps, append '?simple=true' to any Zap. E.g. localhost:8000/app/editor/933?simple=true
export const hasSimpleQueryParam = (query: { simple?: string }) =>
  _.get(query, ['simple'], false) === 'true';

export const selectSharedZapStepIndexes = createSelector(
  selectCurrentSharedZap,
  selectFlowWithSteps,
  selectShouldUseSimpleZapSetup,
  (sharedZap, flow, shouldUseSimpleZapSetup) => {
    if (!sharedZap || !shouldUseSimpleZapSetup || !flow) {
      return {};
    }
    return mapNodeIdsToSharedZapStepIndexes(flow, sharedZap);
  }
);

const selectShouldUseDefaults = createSelector(
  selectNode,
  (node: FlowNode) =>
    !_.get(node, ['meta', 'validation', 'params', 'is_opened'])
);

export const selectFormaticOptions = createSelector(
  selectShouldUseDefaults,
  selectIsFlowTemplate,
  selectShouldUseSimpleZapSetup,
  (shouldUseDefaults, isZapTemplate, shouldUseSimpleZapSetup) => ({
    shouldUseDefaults,
    isZapTemplate,
    shouldUseSimpleZapSetup,
  })
);

export const getIncidents = (state: State) =>
  _.get(state, ['flow', 'incidents']);

export const selectOpenAddStepPickerNodeId = (state: State, flowId: ID) =>
  _.get(state, ['flow', 'flows', flowId, 'openAddStepPickerNodeId']);

export const selectIsAddingOrRemovingStep = (state: State, flowId: ID) =>
  _.get(state, ['flow', 'flows', flowId, 'isAddingOrRemovingStep']);

export const selectNote = (nodes: NodeMap) => _.get(getRootNode(nodes), 'note');

export const selectTimezone = (flow?: Flow) => _.get(flow, 'timezone');

export const selectAreGivesLoading = (state: State, flowId: ID, nodeId: ID) =>
  _.get(state, ['flow', 'flows', flowId, 'givesLoading', nodeId], false);

export const selectIsSwitchingEditors = (state: State) =>
  _.get(state, ['flow', 'isSwitchingEditors'], false);

export const selectPublicZapRecipeId = (publicZap: PublicZap) =>
  _.get(publicZap, ['guided_recipe', 'id']);

export const hasPublicMeta = (flow?: Flow) => !!_.get(flow, ['publicMeta']);

export const selectFlowTemplateId = (state: State, flowId: ID) => {
  const flow = selectFlow(flowId, state) || { id: flowId };
  const rootNode = getRootNode(flow.nodes);
  return _.get(rootNode, 'guided_recipe_origin_id');
};

export const selectPublicGuidFromFlow = (state: State, flowId: ID) => {
  const templateId = selectFlowTemplateId(state, flowId);
  const sharedZap = selectSharedZapById(templateId)(state);
  return _.get(sharedZap, ['link_hash']);
};

export const selectAreEditorErrorsVisible = (state: State) =>
  _.get(state, ['flow', 'shouldShowEditorErrors']);

// Ensure that empty dismissed errors are always equal by reference
const EMPTY_DISMISSED_ERRORS = [];
export const selectDismissedErrors = (state: State) => {
  return _.get(state, ['flow', 'dismissed'], EMPTY_DISMISSED_ERRORS);
};

export const selectRouterNodeId = (state: State) =>
  _.get(state, ['router', 'current', 'nodeId']);

const selectAccount = state => state.account;

export const selectEditorAccount = createSelector(
  selectAccount,
  legacyAccountAndProfileCombo,
  (account, legacyAccountAndProfile) => {
    return {
      ...account,
      account: legacyAccountAndProfile,
    };
  }
);

export const selectScrollToNodeId = (state: State, flowId: ID) =>
  _.get(state, ['flow', 'scrollToNodeIds', flowId]);

export const selectCurrentNode = (state: State): ?FlowNode => {
  const flowId = getParam('id', state);
  const nodeId = getParam('nodeId', state);
  return selectNode(state, { flowId, nodeId });
};
